import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Crown, X, BarChart3, Calendar, Zap, 
  CheckCircle, AlertCircle, Star, TrendingUp
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserDashboard = ({ isOpen, onClose, userEmail, onUpgrade }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userEmail) {
      fetchSubscription();
    }
  }, [isOpen, userEmail]);

  const fetchSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/subscription/${userEmail}`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getPlanColor = (plan) => {
    switch (plan) {
      case "lifetime": return "#ffd700";
      case "pro": return "#ccff00";
      case "student": return "#a855f7";
      default: return "#888";
    }
  };

  const getPlanIcon = (plan) => {
    switch (plan) {
      case "lifetime": return "👑";
      case "pro": return "⚡";
      case "student": return "🎓";
      default: return "🆓";
    }
  };

  return (
    <AnimatePresence>
      <div className="modal-overlay" onClick={onClose} data-testid="dashboard-overlay">
        <motion.div 
          className="dashboard-modal"
          onClick={e => e.stopPropagation()}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          data-testid="user-dashboard"
          style={{
            background: 'linear-gradient(135deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)',
            borderRadius: '20px',
            padding: '0',
            maxWidth: '500px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'hidden',
            border: '1px solid rgba(204,255,0,0.2)',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.8)'
          }}
        >
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(204,255,0,0.15) 0%, rgba(255,0,102,0.1) 100%)',
            padding: '24px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ccff00, #ff0066)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem'
              }}>
                {subscription ? getPlanIcon(subscription.plan) : '👤'}
              </div>
              <div>
                <h2 style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>
                  Your Dashboard
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', margin: 0 }}>
                  {userEmail}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff'
              }}
              data-testid="dashboard-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 180px)' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                <div className="spinner" style={{ margin: '0 auto 16px' }} />
                Loading your data...
              </div>
            ) : subscription ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* Plan Card */}
                <div style={{
                  background: `linear-gradient(135deg, ${getPlanColor(subscription.plan)}15 0%, rgba(0,0,0,0.3) 100%)`,
                  border: `2px solid ${getPlanColor(subscription.plan)}40`,
                  borderRadius: '16px',
                  padding: '20px'
                }} data-testid="plan-card">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Crown className="w-5 h-5" style={{ color: getPlanColor(subscription.plan) }} />
                      <span style={{ color: '#fff', fontWeight: 600 }}>Current Plan</span>
                    </div>
                    {subscription.badge && (
                      <span style={{
                        background: getPlanColor(subscription.plan),
                        color: '#000',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 700
                      }}>
                        {subscription.badge}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: getPlanColor(subscription.plan) }}>
                    {subscription.plan_name}
                  </div>
                  {subscription.is_owner && (
                    <div style={{ 
                      marginTop: '8px', 
                      color: '#ffd700', 
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <Star className="w-4 h-4" /> Owner Account - Lifetime Access
                    </div>
                  )}
                </div>

                {/* Usage Stats */}
                <div style={{
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '16px',
                  padding: '20px'
                }} data-testid="usage-stats">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <BarChart3 className="w-5 h-5" style={{ color: '#00f0ff' }} />
                    <span style={{ color: '#fff', fontWeight: 600 }}>Usage This Month</span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '8px',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.9rem'
                    }}>
                      <span>Analyses Used</span>
                      <span style={{ fontWeight: 600, color: '#fff' }}>
                        {subscription.analyses_used} / {subscription.analyses_limit === -1 ? '∞' : subscription.analyses_limit}
                      </span>
                    </div>
                    <div style={{
                      height: '8px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: subscription.analyses_limit === -1 
                          ? '10%' 
                          : `${Math.min(100, (subscription.analyses_used / subscription.analyses_limit) * 100)}%`,
                        background: subscription.analyses_limit === -1 
                          ? 'linear-gradient(90deg, #ccff00, #00f0ff)'
                          : subscription.analyses_used >= subscription.analyses_limit 
                            ? '#ff4444' 
                            : 'linear-gradient(90deg, #ccff00, #00f0ff)',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>

                  {/* Status Indicator */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    background: subscription.can_analyze 
                      ? 'rgba(34,197,94,0.1)' 
                      : 'rgba(255,68,68,0.1)',
                    borderRadius: '8px',
                    border: `1px solid ${subscription.can_analyze ? 'rgba(34,197,94,0.3)' : 'rgba(255,68,68,0.3)'}`
                  }}>
                    {subscription.can_analyze ? (
                      <>
                        <CheckCircle className="w-5 h-5" style={{ color: '#22c55e' }} />
                        <span style={{ color: '#22c55e', fontWeight: 500 }}>
                          {subscription.analyses_limit === -1 
                            ? 'Unlimited analyses available' 
                            : `${subscription.analyses_remaining} analyses remaining`}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-5 h-5" style={{ color: '#ff4444' }} />
                        <span style={{ color: '#ff4444', fontWeight: 500 }}>
                          Monthly limit reached - Upgrade to continue!
                        </span>
                      </>
                    )}
                  </div>

                  {/* Reset Date */}
                  {subscription.analyses_limit !== -1 && (
                    <div style={{ 
                      marginTop: '12px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.85rem'
                    }}>
                      <Calendar className="w-4 h-4" />
                      Resets in {subscription.days_until_reset} days
                    </div>
                  )}
                </div>

                {/* Features */}
                <div style={{
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '16px',
                  padding: '20px'
                }} data-testid="features-list">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <Zap className="w-5 h-5" style={{ color: '#ccff00' }} />
                    <span style={{ color: '#fff', fontWeight: 600 }}>Your Features</span>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <FeatureRow 
                      label="Max Prompts per Analysis" 
                      value={subscription.max_prompts >= 99999 ? 'Unlimited' : subscription.max_prompts} 
                      enabled={true}
                    />
                    <FeatureRow 
                      label="All 6 Platforms" 
                      value={subscription.platforms === 'all' ? 'Yes' : 'Limited'} 
                      enabled={subscription.platforms === 'all'}
                    />
                    <FeatureRow 
                      label="AI-Enhanced Prompts" 
                      value={subscription.ai_enhanced ? 'Enabled' : 'Disabled'} 
                      enabled={subscription.ai_enhanced}
                    />
                    <FeatureRow 
                      label="Priority Support" 
                      value={subscription.priority_support ? 'Enabled' : 'Disabled'} 
                      enabled={subscription.priority_support}
                    />
                  </div>
                </div>

                {/* Upgrade Button (for free users) */}
                {subscription.plan === 'free' && (
                  <button
                    onClick={() => {
                      onClose();
                      onUpgrade();
                    }}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #ccff00 0%, #ff0066 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#000',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}
                    data-testid="upgrade-btn"
                  >
                    <TrendingUp className="w-5 h-5" />
                    Upgrade to Pro - Unlimited Everything
                  </button>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                Unable to load subscription data
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const FeatureRow = ({ label, value, enabled }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '8px'
  }}>
    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>{label}</span>
    <span style={{ 
      color: enabled ? '#ccff00' : 'rgba(255,255,255,0.4)', 
      fontWeight: 600,
      fontSize: '0.9rem'
    }}>
      {value}
    </span>
  </div>
);

export default UserDashboard;
